import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  assessDevice,
  probeDevice,
  type DeviceAssessment,
} from "./device-probe";
import { reportDeviceProbe } from "./telemetry";

/**
 * Runs the device probe exactly once per app load, then makes the
 * assessment available app-wide via useDevice(). Also fires the
 * `device_probe` telemetry event.
 */

const DISMISS_KEY = "telecrypt.device-warning.dismissed-at";

interface DeviceApi {
  assessment: DeviceAssessment | null;
  /** True if the user has already seen + dismissed the warning. */
  warningDismissed: boolean;
  dismissWarning: () => void;
}

const Ctx = createContext<DeviceApi | null>(null);

export function DeviceProvider({ children }: { children: ReactNode }) {
  const [assessment, setAssessment] = useState<DeviceAssessment | null>(null);
  const [warningDismissed, setWarningDismissed] = useState<boolean>(() => {
    if (typeof localStorage === "undefined") return false;
    return !!localStorage.getItem(DISMISS_KEY);
  });

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const probe = await probeDevice();
        const a = assessDevice(probe);
        if (cancelled) return;
        setAssessment(a);
        reportDeviceProbe(a);
      } catch {
        // probe is best-effort; assessment stays null
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const api = useMemo<DeviceApi>(
    () => ({
      assessment,
      warningDismissed,
      dismissWarning: () => {
        if (typeof localStorage !== "undefined") {
          localStorage.setItem(DISMISS_KEY, String(Date.now()));
        }
        setWarningDismissed(true);
      },
    }),
    [assessment, warningDismissed],
  );

  return <Ctx.Provider value={api}>{children}</Ctx.Provider>;
}

export function useDevice(): DeviceApi {
  const v = useContext(Ctx);
  if (!v) throw new Error("useDevice must be used inside <DeviceProvider>");
  return v;
}
